<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      fullscreen
      persistent
      scrollable
    >
      <v-form
        ref="form"
      >
        <v-card>
          <v-card-title>
            <v-row>
              <v-col>
                <span class="headline">{{ isClone ? 'Clone' : poNumber ? 'Update' : 'Create' }} PO </span> <span
                  v-if="poNumber"
                  style="font-weight: bold; font-size: 24px;"
                >#{{ poNumber }}</span>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text>
            <v-row align="center">
              <v-col
                cols="2"
              >
                <inventory-class-select-element
                  v-model="inventoryClassId"
                  :is-required="true"
                  :is-add-all="false"
                  :disabled="isClone"
                />
              </v-col>
              <v-col cols="3">
                <v-autocomplete
                  attach
                  v-model="supplierId"
                  label="Supplier"
                  :items="suppliers"
                  :rules="[(v) => !!v || 'Supplier is required']"
                  :item-text="item => `${item.code} ${item.name}`"
                  item-value="id"
                  clearable
                  :single-select="false"
                  :disabled="isClone"
                >
                  <template #selection="{ item }">
                    <span>
                      <span class="font-weight-bold">{{ item.code }}</span> {{ item.name }}
                    </span>
                  </template>
                  <template #item="{ item }">
                    <span>
                      <span class="font-weight-bold">{{ item.code }}</span> {{ item.name }}
                    </span>
                  </template>
                  <template slot="label">
                    Supplier<RedAsterisk />
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="3">
                <v-autocomplete
                  attach
                  v-model="jobId"
                  :label="jobLabel"
                  :items="jobs"
                  :item-text="item => `${item.code} ${item.name}`"
                  :rules="[(v) => !!v || `${jobLabel} is required`]"
                  item-value="id"
                  clearable
                  :single-select="false"
                  :loading="jobsLoading"
                >
                  <template #selection="{ item }">
                    <span>
                      <span style="font-weight: bold;">{{ item.code ? `${item.code}` : '' }}</span> - {{ item.name ? item.name : '' }} <span style="color: #1976D2;">{{ item.reference ? `; ${item.reference}` : '' }}</span>
                    </span>
                  </template>

                  <template #item="{ item }">
                    <span>
                      <span style="font-weight: bold;">{{ item.code ? `${item.code}` : '' }}</span> - {{ item.name ? item.name : '' }}<span style="color: #1976D2;">{{ item.reference ? `; ${item.reference}` : '' }}</span>
                    </span>
                  </template>
                  <template slot="label">
                    {{ jobLabel }}<RedAsterisk />
                  </template>
                  <template #append-outer>
                    <v-btn
                      class="mrp-btn-add"
                      color="primary"
                      outlined
                      small
                      @click="openJobForm"
                    >
                      <v-icon small>
                        fas fa-plus
                      </v-icon>
                    </v-btn>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col
                cols="2"
              >
                <v-menu
                  ref="menuShipDate"
                  v-model="dateMenuRequiredBy"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="requiredByDate"
                      readonly
                      append-icon="mdi-calendar"
                      v-bind="attrs"
                      :rules="requiredByDateRules"
                      :error="isSealedUnitOverbooked"
                      :error-messages="sealedUnitOverbookedErrorMessages"
                      v-on="on"
                      @change="$refs.menuShipDate.save(requiredByDate)"
                    >
                      <template slot="label">
                        Required by Date<RedAsterisk />
                      </template>
                    </v-text-field>
                  </template>
                  <business-date-picker
                    v-model="requiredByDate"
                    no-title
                    scrollable
                    :min="minRequiredByDate"
                    @input="onRequiredByDateChange"
                  >
                    <v-spacer />
                    <v-btn
                      text
                      color="primary"
                      @click="dateMenuRequiredBy = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="requiredByDate = null; resetSealedUnitOverbookedValidation()"
                    >
                      Clear
                    </v-btn>
                  </business-date-picker>
                </v-menu>
              </v-col>
              <v-col
                v-if="!isClone"
                class="text-end"
              >
                <v-btn
                  color="primary"
                  outlined
                  :disabled="!jobId || !supplierId || !inventoryClassId"
                  @click="openGlassCodeForm"
                >
                  <v-icon
                    small
                    class="mr-1"
                  >
                    fas fa-plus
                  </v-icon>
                  New Glass Item
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                class="pt-0"
              >
                <v-card
                  elevation="0"
                  outlined
                >
                  <v-data-table
                    :headers="headers"
                    :items="glassItems"
                    hide-default-footer
                    :items-per-page="-1"
                    dense
                    :expanded.sync="expandedItems"
                    item-key="temp_id"
                  >
                    <template #[`item.glass_thickness`]="{ item }">
                      <v-autocomplete
                        attach
                        v-if="item.isClone"
                        v-model="item.glass_thickness"
                        :items="glassThicknesses"
                        item-text="name"
                        item-value="name"
                        :disabled="item.glass.glass_code_type_id === glassTypeIdSingle || item.glass.glass_code_type_id === glassTypeIdSpandler"
                      />
                      <span
                        v-else
                        style="font-weight: bold;"
                      >{{ item.glass_thickness }}</span>
                    </template>
                    <template #[`item.glass_type`]="{ item }">
                      <v-autocomplete
                        attach
                        v-if="item.isClone"
                        v-model="item.glass.glass_code_type_id"
                        :items="glassTypes"
                        item-text="name"
                        item-value="id"
                        @change="getGlassPaintCodes(item)"
                      />
                      <span
                        v-else
                        style="font-weight: bold;"
                      >{{ item.glass_type }}</span>
                    </template>
                    <template #[`item.g_code`]="{ item }">
                      <v-autocomplete
                        attach
                        v-if="item.isClone"
                        v-model="item.g_code"
                        :items="item.g_codes"
                        item-text="row"
                        item-value="row"
                        :loading="item.glassPaintCodesLoading"
                        :disabled="item.glassPaintCodesLoading"
                      />
                      <span
                        v-else
                        style="font-weight: bold;"
                      >{{ item.g_code }}</span>
                    </template>
                    <template #[`item.quantity`]="{ item }">
                      <v-text-field
                        v-if="item.isClone"
                        v-model="item.quantity"
                        min="0"
                        type="number"
                      />
                      <span
                        v-else
                      >{{ item.quantity }}</span>
                    </template>
                    <template #[`item.width`]="{ item }">
                      <span>{{ item.width ? (uomSwitch ? convertToFraction(item.width) : item.width) : '-' }}</span>
                    </template>
                    <template #[`item.height`]="{ item }">
                      <span>{{ item.height ? (uomSwitch ? convertToFraction(item.height) : item.height) : '-' }}</span>
                    </template>
                    <template #[`item.offset`]="{ item }">
                      <v-tooltip v-if="item.offset || item.double_offset" color="black" bottom>
                        <template #activator="{ on, attrs }">
                          <div v-on="on" v-bind="attrs">
                            <v-icon
                              style="font-size: 20px; color: #62CE6D;"
                            >
                              mdi-check
                            </v-icon>
                          </div>
                        </template>
                        <template>
                          <div v-if="item.offset">Offset</div>
                          <div v-if="item.double_offset">Double Offset</div>
                          <div v-if="item.is_painted">Painted</div>
                          <div v-if="item.is_painted">
                            Painted Lite: {{ item.glass_painted_lites ? item.glass_painted_lites.map(lite => lite.name).join(', ') : '' }}
                          </div>
                          <div v-if="item.offset_width">Offset Width: {{ item.offset_width }}</div>
                          <div v-if="item.offset_height">Offset Height: {{ item.offset_height }}</div>
                          <div v-if="item.glass_offset_lite_id">
                            Offset Lite: {{ item.glass_offset_lite_id ? getGlassOffsetLiteName(item.glass_offset_lite_id) : '' }}
                          </div>
                        </template>
                      </v-tooltip>
                    </template>
                    <template #[`item.painted_su`]="{ item }">
                      <v-icon
                        v-if="item.is_painted"
                        style="font-size: 20px; color: #62CE6D;"
                      >
                        mdi-check
                      </v-icon>
                    </template>
                    <template #[`item.status`]="{ item }">
                      <v-chip
                        v-if="item.glass_id"
                        small
                        color="orange"
                        text-color="white"
                      >
                        ACTIVE
                      </v-chip>
                      <v-chip
                        v-else
                        small
                        color="success"
                        text-color="white"
                      >
                        READY FOR IMPORT
                      </v-chip>
                    </template>
                    <template
                      v-if="!isClone"
                      #[`item.actions`]="{ item }"
                    >
                      <v-btn
                        v-if="!isClone"
                        class="ml-1 mt-1 mb-1"
                        color="purple"
                        outlined
                        small
                        @click="cloneGlassItem(item)"
                      >
                        CLONE
                      </v-btn>
                      <v-btn
                        v-if="!item.isClone"
                        class="ml-1 mt-1 mb-1"
                        color="primary"
                        outlined
                        small
                        @click="editGlassItem(item)"
                      >
                        EDIT
                      </v-btn>
                      <v-btn
                        class="ml-1 mt-1 mb-1"
                        color="error"
                        outlined
                        small
                        @click="deleteGlassItem(item)"
                      >
                        DELETE
                      </v-btn>
                    </template>
                    <template
                      v-if="!isClone"
                      #expanded-item="{ item }"
                    >
                      <td
                        v-if="item.glass && !item.isClone"
                        :colspan="13"
                        class="pl-0 pr-0"
                      >
                        <v-container
                          fluid
                          :style="{backgroundColor: isDarkMode ? '#556B2F !important' : '#E8F5E9 !important'}"
                        >
                          <v-row>
                            <v-col
                              v-if="item.glass.part_number"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Part Number:</span> <span>{{ item.glass.part_number }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.awd_gas"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">AWD Gas:</span> <span>{{ item.glass.awd_gas }} - {{ getGlassDescription(item.glass.awd_gas) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.awd_spacer"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">AWD Spacer:</span> <span>{{ item.glass.awd_spacer }} - {{ getGlassDescription(item.glass.awd_spacer) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.cen_heat_treatment"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Cen Heat Treatment:</span> <span>{{ item.glass.cen_heat_treatment }} - {{ getGlassDescription(item.glass.cen_heat_treatment ) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.center_glass"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Center Glass:</span> <span>{{ item.glass.center_glass }} - {{ getGlassDescription(item.glass.center_glass) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.double_triple"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Double/Triple:</span> <span>{{ item.glass.double_triple }} - {{ getGlassDescription(item.glass.double_triple) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.ext_heat_treatment"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Ext Heat Treatment:</span> <span>{{ item.glass.ext_heat_treatment }} - {{ getGlassDescription(item.glass.ext_heat_treatment) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.exterior_glass"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Exterior Glass:</span> <span>{{ item.glass.exterior_glass }} - {{ getGlassDescription(item.glass.exterior_glass) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.fritted"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Fritted:</span> <span>{{ item.glass.fritted }} - {{ getGlassDescription(item.glass.fritted ) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.int_heat_treatment"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Int Heat Treatment:</span> <span>{{ item.glass.int_heat_treatment }} - {{ getGlassDescription(item.glass.int_heat_treatment) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.interior_glass"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Interior Glass:</span> <span>{{ item.glass.interior_glass }} - {{ getGlassDescription(item.glass.interior_glass) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.le_surface"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">LE Surface:</span> <span>{{ item.glass.le_surface }} - {{ getGlassDescription(item.glass.le_surface) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.middle_glass"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Middle Glass:</span> <span>{{ item.glass.middle_glass }} - {{ getGlassDescription(item.glass.middle_glass) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.edge_delete"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Edge Delete:</span> <span>{{ item.glass.edge_delete }} - {{ getGlassDescription(item.glass.edge_delete) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.sgl_heat_treatment"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Sgl Heat Treatment:</span> <span>{{ item.glass.sgl_heat_treatment }} - {{ getGlassDescription(item.glass.sgl_heat_treatment) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.single_glass"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Single Glass:</span> <span>{{ item.glass.single_glass }} - {{ getGlassDescription(item.glass.single_glass) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.spandrel_paint"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">Spandrel Paint:</span> <span>{{ item.glass.spandrel_paint }} - {{ getGlassDescription(item.glass.spandrel_paint) }}</span>
                            </v-col>
                            <v-col
                              v-if="item.glass.stc"
                              class="pr-0 pl-2"
                              cols="auto"
                            >
                              <span :style="{color: isDarkMode ? 'white' : 'rgba(0,0,0,0.6)'}">STC:</span> <span>{{ item.glass.stc }} - {{ getGlassDescription(item.glass.stc) }}</span>
                            </v-col>
                          </v-row>
                        </v-container>
                      </td>
                    </template>
                  </v-data-table>
                </v-card>
              </v-col>
            </v-row>
            <v-row
              v-if="!isClone"
            >
              <v-spacer />
              <v-col
                cols="auto"
                class="pb-0 pt-0"
              >
                <v-checkbox
                  v-model="isRemake"
                  label="Remake"
                  @change="handleRemakeChange"
                />
              </v-col>
              <v-col
                cols="3"
                class="pb-0 pt-0 mr-6"
              >
                <v-select
                  v-model="remakeReasonId"
                  :items="remakeReasons"
                  label="Remake Main Category"
                  item-text="name"
                  item-value="id"
                  :disabled="!isRemake"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="!isClone && remakeReasonId && isRemake"
            >
              <v-spacer />
              <v-col
                cols="3"
                class="pb-0 pt-0 mr-6"
              >
                <v-select
                  v-model="remakeSubReasonId"
                  :items="remakeSubReasons"
                  label="Remake Sub Category"
                  item-text="name"
                  item-value="id"
                  :disabled="!isRemake"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="!isClone"
            >
              <v-spacer />
              <v-col
                cols="auto"
                class="pb-0 pt-0"
              >
                <v-row align="center">
                  <v-col class="pr-2 pt-6">
                    <span :style="{color: isDarkMode ? '#ffffffb3' : '#00000099', fontSize: '16px !important'}">Decimal</span>
                  </v-col>
                  <v-col class="pl-0">
                    <v-switch
                      v-model="uomSwitch"
                      hide-details
                      class="mt-0 pt-3"
                      label="Fraction"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col
                cols="auto"
              >
                <v-row>
                  <v-spacer />
                  <v-col
                    cols="auto"
                    class="text-end pr-2"
                  >
                    <span :style="{ color: isDarkMode ? '' : '#000000', fontWeight: 'bold', fontSize: '18px' }">Total Glass Item Qty:</span>
                  </v-col>
                  <v-col
                    cols="auto"
                    class="text-start pl-0 mr-6"
                  >
                    <span :style="{ color: isDarkMode ? '' : '#000000', fontWeight: 'bold', fontSize: '18px' }">{{ glassItems.reduce((acc, obj) => acc + parseInt(obj.quantity, 10) , 0) }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-spacer />
                  <v-col
                    cols="auto"
                    class="text-end pr-2"
                  >
                    <span :style="{ color: isDarkMode ? '' : '#000000', fontWeight: 'bold', fontSize: '18px' }">Total Records:</span>
                  </v-col>
                  <v-col
                    cols="auto"
                    class="text-start pl-0 mr-6"
                  >
                    <span :style="{ color: isDarkMode ? '' : '#000000', fontWeight: 'bold', fontSize: '18px' }">{{ glassItems.length }}</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="normal"
              class="mr-2"
              @click="dialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              :loading="isCheckingSealedUnitCount"
              @click="submit"
            >
              SUBMIT
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
    <job-form
      ref="jobForm"
      :is-supervisor="false"
      :is-client="false"
      :is-job-name="false"
      :is-address="false"
      @success="jobSuccess"
    />
    <glass-p-o-item-form
      ref="glassPOItemForm"
      :glass-items="glassItems"
      @success-po="glassPoItemSuccess"
    />
    <confirm-form ref="confirmForm" />
  </v-row>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import JobForm from '@/components/forms/jobs/JobForm';
import GlassPOItemForm from '@/components/forms/glasses/GlassPOItemForm';
import { addBusinessDays } from '@/helpers/BusinessDateHelper';
import BusinessDatePicker from '@/components/elements/BusinessDatePicker.vue';

export default {
  components: {
    BusinessDatePicker,
    JobForm,
    GlassPOItemForm,
  },
  props: {

  },
  data: () => ({
    inventoryClassId: null,
    jobId: null,
    supplierId: 1,
    remakeReasonId: null,
    remakeSubReasonId: null,
    glassItems: [],
    jobs: [],
    suppliers: [],
    glassSpecifications: [],
    glasses: [],
    glassTypes: [],
    glassThicknesses: [],
    glassOffsetLites: [],
    remakeReasons: [],
    dialog: false,
    expandedItems: [],
    loading: false,
    uomSwitch: true,
    jobsLoading: false,
    poNumber: null,
    isClone: false,
    isRemake: false,
    glassLoading: false,
    glassTypeIdSpandler: 1,
    glassTypeIdSealed: 2,
    glassTypeIdSingle: 3,
    supplierIdAwd001: 1,
    gCodesTypeSpandler: [],
    gCodesTypeSealed: [],
    gCodesTypeSingle: [],
    remakeSubReasons: [],
    remakeSubReasonsAll: [],
    dateMenuRequiredBy: false,
    requiredByDate: null,
    isCheckingSealedUnitCount: false,
    isSealedUnitOverbooked: false,
    sealedUnitOverbookedMessage: '',
    maximumSealedUnitsAllowedPerDay: 300,

  }),
  computed: {
    jobLabel() {
      return this.$store.getters['businessRulesGetBoolean']('Jobs Rename to Release')
        ? 'Release'
        : 'Job'; 
    },
    ...mapState({
      isDarkMode: 'isDarkMode',
      holidaysDateToNameMap: (state) => state.holidaysDateToNameMap,
    }),
    ...mapGetters([
    'businessRulesGetBoolean',
    ]),

    isSealedUnitOverbookingCheckEnabled() {
      return !!this.businessRulesGetBoolean('Sealed Unit Overbooking Check');
    },

    headers() {
      const headers = [];
      if (!this.isClone) {
        headers.push({
          text: 'Status', value: 'status', width: '10%', align: 'center',
        });
      }

      if (!this.isClone) {
        headers.push({ text: 'G Code', value: 'g_code' });
        headers.push({ text: 'Glass Type', value: 'glass_type' });
        headers.push({ text: 'Glass Thickness', value: 'glass_thickness' });
      } else {
        headers.push({ text: 'Glass Thickness', value: 'glass_thickness', sortable: false });
        headers.push({ text: 'Glass Type', value: 'glass_type', sortable: false });
        headers.push({ text: 'G Code', value: 'g_code', sortable: false });
      }
      headers.push({ text: 'Tag#', value: 'tag_number', sortable: false });
      headers.push({ text: 'Width', value: 'width', sortable: false });
      headers.push({ text: 'Height', value: 'height', sortable: false });
      headers.push({ text: 'Price/SQFT', value: 'price', sortable: false });
      headers.push({
        text: 'Qty', value: 'quantity', width: '5%', sortable: false,
      });
      headers.push({ text: 'Offset', value: 'offset', width: '3%', align: 'center', sortable: false });
      headers.push({ text: 'Painted S/U?', value: 'painted_su', width: '3%', align: 'center', sortable: false });
      if (!this.isClone) {
        headers.push({ text: 'Note', value: 'holes', sortable: false });
      }
      headers.push({
        text: 'Actions', value: 'actions', align: 'end', sortable: false,
      });

      return headers;
    },
    minRequiredByDate() {
      return addBusinessDays(moment().format('YYYY-MM-DD'), 4, this.holidaysDateToNameMap);
    },
    sealedUnitOverbookedErrorMessages() {
      if (!this.isSealedUnitOverbooked) return [];
      return ['Sealed Units overbooked for this date'];
    },
    requiredByDateRules() {
      return [
        (v) => !!v || 'Required by Date is required',
        () => !this.isSealedUnitOverbooked || 'Sealed Units overbooked for this date',
      ];
    },
  },
  watch: {
    inventoryClassId() {
      this.getJobs();
    },

    remakeReasonId() {
      this.remakeSubReasons = this.remakeSubReasonsAll.filter((x) => x.glass_remake_reason_id === this.remakeReasonId);
    },

    glassItems: {
      deep: true, // Watch nested properties
      handler(newItems) {
        if (this.isClone) {
          newItems.forEach((item) => {
            if (item.glass.glass_code_type_id === this.glassTypeIdSingle) {
              const glassThickness = this.glassThicknesses.find((x) => x.name === 'SINGLE GLASS');
              item.glass_thickness = glassThickness ? glassThickness.name : null;
            } else if (item.glass.glass_code_type_id === this.glassTypeIdSpandler) {
              const glassThickness = this.glassThicknesses.find((x) => x.name === 'SPG 6MM');
              item.glass_thickness = glassThickness ? glassThickness.name : null;
            }
          });
        }
      },
    },
  },
  created() {
    // this.init();
  },
  methods: {

    init() {
      this.getJobs();
      this.getSuppliers();
      this.getGlasses();
      this.getRemakeReasons();
      this.getRemakeSubReasons();
      this.getGlassOffsetLites();
      this.$store.dispatch('getHolidays');
      if (this.isClone) {
        this.getGlassTypes();
      }
    },

    async submit() {
      if (!await this.$refs.form.validate()) return;
      if (this.glassItems.length === 0) {
        this.$bus.$emit('showError', 'Please add at least one glass item.');
        return;
      }
      if (this.isSealedUnitOverbookingCheckEnabled) {
        if (this.isCheckingSealedUnitCount) {
          this.$bus.$emit('showError', 'Checking sealed unit count, please wait.');
          return;
        }
        const isValidSealedUnitDate = await this.checkSealedUnitProductionCountForDate(this.requiredByDate);
        if (!isValidSealedUnitDate) return;
      }

      this.$bus.$emit('loading');
      const glssItems = this.glassItems.map((item) => {
        const newItem = { ...item };
        const job = this.jobs.find((j) => j.id === this.jobId);
        newItem.job_code = job ? job.code : null;
        newItem.job_id = job ? job.id : null;
        newItem.supplier_id = this.supplierId;
        newItem.inventory_class_id = this.inventoryClassId;
        newItem.required_date = this.requiredByDate;
        newItem.remake_reason_id = this.remakeReasonId;
        newItem.remake_sub_reason_id = this.remakeSubReasonId;
        if (this.poNumber) newItem.po_number = this.isClone ? null : this.poNumber;
        return newItem;
      });
      const res = await this.$api.glassPurchaseOrderItem.store(glssItems);

      if (!res) {
        this.$bus.$emit('showError');
        this.$bus.$emit('loading');
        return;
      }
      this.$bus.$emit('loading');
      this.$emit('success');
      this.dialog = false;
    },

    async getJobs() {
      this.jobsLoading = true;
      const submitModel = {
        filters: { inventory_class_id: this.inventoryClassId },
      };
      const res = await this.$api.job.readDDL(submitModel);
      const cancelCode = 3001;
      if (res === cancelCode) return;
      this.jobs = res;
      this.jobsLoading = false;
    },

    async getSuppliers() {
      this.suppliers = await this.$api.supplier.get();
    },

    async getGlasses() {
      this.glassSpecifications = await this.$api.glass.readSpecifications({ filters: { } });
    },

    async getRemakeReasons() {
      this.remakeReasons = await this.$api.glassRemakeReason.get();
    },

    async getRemakeSubReasons() {
      this.remakeSubReasonsAll = await this.$api.glassRemakeSubReason.get();
    },

    async getGlassPaintCodes(item) {
      item.glassPaintCodesLoading = true;
      const submitModel = {
        filters: {
          glass_type_id: item.glass.glass_code_type_id,
          is_manual: 0,
        },
      };
      const res = await this.$api.glass.readGlassPaintCode(submitModel);
      const cancelCode = 3001;
      if (res === cancelCode) return;
      if (!res) return;
      this.$set(item, 'g_codes', res);
      item.glassPaintCodesLoading = false;
    },

    async getGlassTypes() {
      this.glassTypes = await this.$api.glass.get('glass-code-types');
    },

    async getGlassThickness() {
      this.glassThicknesses = await this.$api.glass.get('glass-thicknesses');
    },

    async getGlassOffsetLites() {
      this.glassOffsetLites = await this.$api.glass.get('glass-offset-lites');
    },

    getGlassDescription(code) {
      const glassSpecification = this.glassSpecifications.find((x) => x.code === code);
      if (!glassSpecification) return '';
      return glassSpecification.description;
    },

    getGlassOffsetLiteName(id) {
      const glassOffsetLite = this.glassOffsetLites.find((x) => x.id === id);
      if (!glassOffsetLite) return '';
      return glassOffsetLite.name;
    },

    async open(inventoryClassId, poNumber, isClone = false) {
      this.$bus.$emit('loading');
      this.resetSealedUnitOverbookedValidation();
      this.poNumber = null;
      this.isClone = isClone;
      this.glassItems = [];
      if (this.$refs.form) this.$refs.form.reset();
      if (poNumber) {
        this.loading = true;
        const submitModel = {
          filters: {
            po_number: poNumber,
            inventory_class_id: inventoryClassId,
            is_not_include_with_batch: this.isClone ? null : 1,
            is_group_remake: 1,
            is_all: 1,
            is_not_include_offset_child: 1,
          },
        };
        const res = await this.$api.glass.readGlass(submitModel);
        const cancelCode = 3001;
        if (res === cancelCode) return;
        this.loading = false;
        if (!res || !res.data || res.data.length === 0) {
          this.glassItems = [];
          this.$bus.$emit('loading');
          return;
        }
        this.poNumber = poNumber;

        // all items have the same supplier, jobId and inventoryClassId
        this.supplierId = res.data[0].supplier_id;
        this.inventoryClassId = res.data[0].inventory_class_id;
        this.jobId = res.data[0].job ? res.data[0].job.id : null;
        this.requiredByDate = res.data[0].required_date ? res.data[0].required_date : null;
        this.remakeReasonId = this.isClone ? null : res.data[0].remake_reason_id;
        this.remakeSubReasonId = this.isClone ? null : res.data[0].remake_sub_reason_id;
        this.isRemake = !!this.remakeReasonId;
        this.expandedItems = res.data;
        this.glassItems = res.data;
        if (this.isClone) {
          if (this.supplierId === null) {
            this.supplierId = this.supplierIdAwd001;
          }
          await this.getGlassThickness();
          for (let i = 0; i < this.glassItems.length; i++) {
            await this.attachGCodes(this.glassItems[i]);
            this.appendManualGCode(this.glassItems[i]);
            this.glassItems[i].isClone = true;
          }
        }
      } else {
        this.supplierId = 1;
      }
      this.init();
      this.$bus.$emit('loading');
      this.dialog = true;
    },

    async attachGCodes(glassItem) {
      if (glassItem.glass.glass_code_type_id === this.glassTypeIdSpandler) {
        if (this.gCodesTypeSpandler.length === 0) {
          await this.getGlassPaintCodes(glassItem);
          this.gCodesTypeSpandler = glassItem.g_codes;
        } else {
          glassItem.g_codes = this.gCodesTypeSpandler;
        }
      } else if (glassItem.glass.glass_code_type_id === this.glassTypeIdSealed) {
        if (this.gCodesTypeSealed.length === 0) {
          await this.getGlassPaintCodes(glassItem);
          this.gCodesTypeSealed = glassItem.g_codes;
        } else {
          glassItem.g_codes = this.gCodesTypeSealed;
        }
      } else if (glassItem.glass.glass_code_type_id === this.glassTypeIdSingle) {
        if (this.gCodesTypeSingle.length === 0) {
          await this.getGlassPaintCodes(glassItem);
          this.gCodesTypeSingle = glassItem.g_codes;
        } else {
          glassItem.g_codes = this.gCodesTypeSingle;
        }
      }
    },

    appendManualGCode(glassItem) {
      const { g_code } = glassItem;
      if (!g_code.toLowerCase().startsWith('m')) return;
      glassItem.g_codes.push(glassItem.g_code);
    },

    openGlassCodeForm() {
      this.$refs.glassPOItemForm.open(null, this.supplierId);
    },

    editGlassItem(item) {
      this.$refs.glassPOItemForm.open(item, this.supplierId);
    },

    async deleteGlassItem(item) {
      const confirm = await this.$refs.confirmForm.open({
        title: 'Are you sure that you want to remove this Glass Item?',
      });
      if (!confirm) return;
      const index = this.glassItems.findIndex((x) => x.temp_id === item.temp_id);
      if (index >= 0) {
        this.glassItems.splice(index, 1);
      }
      this.resetSealedUnitOverbookedValidation();
    },

    async openJobForm() {
      var res = null;
      if (this.businessRulesGetBoolean('Desa WO Codes')) {
        res = await this.$api.job.get(`generate-manual-code-desa/P`);
      } else {
        res = await this.$api.job.get('generate-manual-code');
      }
      
      if (!res) return;
      this.$refs.jobForm.open();
      this.$nextTick(() => {
        this.$refs.jobForm.item.code = res;
        this.$refs.jobForm.item.is_manual = 1;
      });
    },

    async jobSuccess() {
      await this.getJobs();
      const latestItemId = Math.max(...this.jobs.map((x) => x.id));
      if (latestItemId) { this.jobId = latestItemId; }
    },

    glassPoItemSuccess(item) {
      const index = this.glassItems.findIndex((x) => x.temp_id === item.temp_id);
      if (index >= 0) {
        this.glassItems.splice(index, 1, item);
      } else {
        this.glassItems.push(item);
      }
      const indexExpand = this.expandedItems.findIndex((x) => x.temp_id === item.temp_id);
      if (indexExpand < 0) {
        this.expandedItems.push(item);
      }
      this.resetSealedUnitOverbookedValidation();
    },

    handleRemakeChange() {
      if (!this.isRemake) {
        this.remakeReasonId = null;
      }
    },

    onRequiredByDateChange(value) {
      console.log('onRequiredByDateChange', value);
      this.$refs.menuShipDate.save(this.requiredByDate);
      this.resetSealedUnitOverbookedValidation();
    },

    async checkSealedUnitProductionCountForDate(date) {
      this.resetSealedUnitOverbookedValidation();
      if (!date) return true;

      const pendingSealedUnitCount = this.getSealedUnitQuantity(this.glassItems);
      if (pendingSealedUnitCount <= 0) return true;

      this.isCheckingSealedUnitCount = true;
      try {
        const submitModel = {
          glass_required_by: date,
          po_number: this.poNumber && !this.isClone ? this.poNumber : null,
        };
        const overbookingCheck = await this.$api.glass.store(submitModel, 'sealed-unit-overbooking-check');

        if (this.requiredByDate !== date) return true;

        if (!overbookingCheck || overbookingCheck.status === 'error') {
          this.$bus.$emit('showError', overbookingCheck && overbookingCheck.message ? overbookingCheck.message : 'Unable to check sealed unit count. Please try again.');
          return false;
        }

        const scheduledSealedUnitCount = parseInt(overbookingCheck.scheduled_sealed_unit_count, 10) || 0;
        const totalSealedUnitCount = scheduledSealedUnitCount + pendingSealedUnitCount;
        const maximumSealedUnitsAllowedPerDay = parseInt(overbookingCheck.maximum_sealed_units_allowed_per_day, 10) || this.maximumSealedUnitsAllowedPerDay;

        this.maximumSealedUnitsAllowedPerDay = maximumSealedUnitsAllowedPerDay;

        if (totalSealedUnitCount > maximumSealedUnitsAllowedPerDay) {
          this.isSealedUnitOverbooked = true;
          this.sealedUnitOverbookedMessage = `Sorry, there will be ${totalSealedUnitCount} Sealed Units scheduled for ${date}. The maximum allowed is ${maximumSealedUnitsAllowedPerDay}. Please select a different date or reduce Sealed Unit quantity.`;
          this.$bus.$emit('showError', this.sealedUnitOverbookedMessage);
          return false;
        }

        this.resetSealedUnitOverbookedValidation();
        return true;
      } finally {
        if (this.requiredByDate === date) {
          this.isCheckingSealedUnitCount = false;
        }
      }
    },

    getSealedUnitQuantity(items) {
      return items.reduce((total, item) => {
        if (!this.isSealedUnitGlassPOItem(item)) return total;
        const quantity = parseInt(item.quantity, 10);
        if (isNaN(quantity) || quantity <= 0) return total;
        return total + quantity;
      }, 0);
    },

    isSealedUnitGlassPOItem(item) {
      if (!item) return false;
      const glassCodeTypeId = item.glass ? item.glass.glass_code_type_id : item.glass_code_type_id;
      return parseInt(glassCodeTypeId, 10) === this.glassTypeIdSealed;
    },

    resetSealedUnitOverbookedValidation() {
      this.isSealedUnitOverbooked = false;
      this.sealedUnitOverbookedMessage = '';
      this.isCheckingSealedUnitCount = false;
    },

    async cloneGlassItem(item) {
      const newItem = { ...item };
      newItem.id = null;
      newItem.temp_id = Date.now();
      this.glassItems.push(newItem);
      const indexExpand = this.expandedItems.findIndex((x) => x === newItem.temp_id);
      if (indexExpand < 0) {
        this.expandedItems.push(newItem);
      }
      this.resetSealedUnitOverbookedValidation();
    },

    convertToFraction(decimal) {
      // check if  a decimal number has numbers after the decimal point
      const decimalString = decimal.toString();
      const decimalPointIndex = decimalString.indexOf('.');

      if (decimalPointIndex === -1) {
        return decimal;
      }
      //

      const fractions = {
        0.0625: '1/16',
        0.125: '1/8',
        0.1875: '3/16',
        0.25: '1/4',
        0.3125: '5/16',
        0.375: '3/8',
        0.4375: '7/16',
        0.5: '1/2',
        0.5625: '9/16',
        0.625: '5/8',
        0.6875: '11/16',
        0.75: '3/4',
        0.8125: '13/16',
        0.875: '7/8',
        0.9375: '15/16',
      };

      // If the decimal is an exact match to any fraction, return the fraction directly
      if (decimal in fractions) {
        return fractions[decimal];
      }

      const wholeNumber = Math.floor(decimal);
      // eslint-disable-next-line no-param-reassign
      decimal -= wholeNumber; // Remove the whole number part

      let closestFraction = Object.keys(fractions)[0];

      // eslint-disable-next-line no-restricted-syntax
      for (const fraction of Object.keys(fractions)) {
        if (Math.abs(decimal - fraction) < Math.abs(decimal - closestFraction)) {
          closestFraction = fraction;
        }
      }

      if (wholeNumber > 0) {
        return `${wholeNumber} - ${fractions[closestFraction]}"`;
      }
      return fractions[closestFraction];
    },

  },
};
</script>
